import type { Traveler } from "@b2bportal/traveler-api";
import { setContextWithKey } from "@checkout/helpers";
import { ParentState, type PartialParentContext } from "@checkout/index";
import type {
  OnInfantModalContinueEvent,
  SelectTravelerEvent,
} from "@checkout/states/common/TravelerInformation/events";
import { assign, type DoneInvokeEvent } from "xstate";
import { getIsLapInfant, seatedInfantsNotAllowed } from "../selectors";
import type { FlightMachineContext } from "@/modules/flight-checkout/types";

// any traveler-related actions should be a part of TravelerInformation/actions.ts in checkout
// The only difference here is adding parity with passengerInformation
export const actions = {
  // Override updateTraveler to update both travelerInformation AND passengerInformation.
  // RBC's flights and cars use passengerInformation.userPassengers, while hotels use only
  // travelerInformation.travelers. This ensures both contexts stay in sync after creating/updating.
  updateTraveler: assign(
    (ctx: PartialParentContext, event: DoneInvokeEvent<Traveler>) => {
      const newTraveler = event.data;
      const { travelers } = ctx[ParentState.travelerInformation];

      const newTravelers = travelers.some(
        (traveler) => traveler.id === newTraveler.id
      )
        ? travelers.map((traveler) =>
            traveler.id === newTraveler.id ? newTraveler : traveler
          )
        : [...travelers, newTraveler];

      // Update travelerInformation context
      const updatedCtx = setContextWithKey(
        ctx,
        ParentState.travelerInformation,
        {
          ...ctx[ParentState.travelerInformation],
          travelers: newTravelers,
          currentTraveler: newTraveler, // Set so selectTraveler can find it
        }
      );

      // Also update passengerInformation.userPassengers for flights/cars.
      // Hotels don't have passengerInformation, so this check protects them.
      const passengerInfo = ctx[ParentState.passengerInformation];
      if (passengerInfo) {
        return setContextWithKey(
          updatedCtx,
          `${ParentState.passengerInformation}.userPassengers`,
          newTravelers
        );
      }

      return updatedCtx;
    }
  ),
  selectTraveler: assign(
    (ctx: PartialParentContext, event: SelectTravelerEvent) => {
      const travelerInformationCtx = ctx[ParentState.travelerInformation];
      const {
        travelerId: eventTravelerId,
        singleTravelerWorkflow,
        onUpdate = false,
      } = event;

      // If no travelerId in event, fallback to currentTraveler.
      // This happens when selectTraveler is called after UPDATE_TRAVELER without an explicit ID.
      const travelerId =
        eventTravelerId ?? travelerInformationCtx.currentTraveler?.id;

      // If still no travelerId, return early
      if (!travelerId) {
        return ctx;
      }

      const { selectedTravelerIds, travelers, selectedLapInfantIds } =
        travelerInformationCtx;
      const newTravelerInfoCtx = { ...travelerInformationCtx };
      let updatedActivityInfo: Record<string, unknown> | undefined;

      // Unselect the traveler if already exists in the selected list and action is not triggered by update
      if (!onUpdate && selectedTravelerIds.includes(travelerId)) {
        newTravelerInfoCtx.selectedTravelerIds = selectedTravelerIds.filter(
          (id) => id !== travelerId
        );
      } else if (!onUpdate && selectedLapInfantIds?.includes(travelerId)) {
        const newSelectedLapInfantIds = selectedLapInfantIds.filter(
          (id) => travelerId !== id
        );
        newTravelerInfoCtx.selectedLapInfantIds = newSelectedLapInfantIds;
      } else {
        // Handling adding new traveler selection
        const newTraveler = travelers.find(
          (traveler) => traveler.id === travelerId
        );
        // shouldn't happen, unknown traveler, do nothing
        if (!newTraveler) return ctx;

        const isNewTravelerInfant = getIsLapInfant(newTraveler);
        // No single traveler Workflow (hotels and cars), only for flights
        if (!singleTravelerWorkflow && isNewTravelerInfant) {
          if (seatedInfantsNotAllowed(ctx as Partial<FlightMachineContext>)) {
            // If the excludeINS is true, we only allow infants to be added as an INF
            newTravelerInfoCtx.selectedLapInfantIds = [
              ...(selectedLapInfantIds ?? []),
              travelerId,
            ];
          } else {
            // Set currentInfantId to open the seat picker modal (in-lap vs own-seat)
            newTravelerInfoCtx.currentInfantId = newTraveler.id;
          }
        } else {
          newTravelerInfoCtx.selectedTravelerIds = singleTravelerWorkflow
            ? [travelerId] // hotel and car have only one primary traveler
            : [...selectedTravelerIds, travelerId]; // air can have multiple travelers
          newTravelerInfoCtx.infantOnlyError = false;
        }
      }

      const currentPassengerInformation = ctx[ParentState.passengerInformation];
      return {
        ...ctx,
        // Sync to passengerInformation for flight checkout compatibility
        // Flight checkout uses both travelerInformation and passengerInformation contexts
        [ParentState.passengerInformation]: {
          ...currentPassengerInformation,
          selectedPassengerIds: newTravelerInfoCtx.selectedTravelerIds,
          selectedLapInfantIds: newTravelerInfoCtx.selectedLapInfantIds,
          infantOnlyError: newTravelerInfoCtx.infantOnlyError,
          currentInfantId: newTravelerInfoCtx.currentInfantId,
        },
        [ParentState.travelerInformation]: newTravelerInfoCtx,
        // Always preserve activityInformation (or use updated version if unselecting)
        [ParentState.activityInformation]:
          updatedActivityInfo ?? ctx[ParentState.activityInformation],
      } as PartialParentContext;
    }
  ),
  /**
   * Re-select a traveler after create/update without toggling.
   * RBC override: syncs to both travelerInformation AND passengerInformation.
   */
  selectTravelerAfterUpdate: assign(
    (ctx: PartialParentContext, event: DoneInvokeEvent<Traveler>) => {
      const updatedTraveler = event.data;
      const travelerInformationCtx = ctx[ParentState.travelerInformation];
      const { selectedTravelerIds, selectedLapInfantIds } =
        travelerInformationCtx;

      // If already selected, keep it selected (don't toggle)
      if (selectedTravelerIds.includes(updatedTraveler.id)) {
        return ctx;
      }

      // If already selected as lap infant, don't also add to selectedTravelerIds
      if (selectedLapInfantIds?.includes(updatedTraveler.id)) {
        return ctx;
      }

      const newTravelerInfoCtx = {
        ...travelerInformationCtx,
        infantOnlyError: false,
      };

      // Infants need the seat picker modal (in-lap vs own-seat) instead
      // of being added directly to selectedTravelerIds
      if (getIsLapInfant(updatedTraveler)) {
        if (seatedInfantsNotAllowed(ctx as Partial<FlightMachineContext>)) {
          newTravelerInfoCtx.selectedLapInfantIds = [
            ...(selectedLapInfantIds ?? []),
            updatedTraveler.id,
          ];
        } else {
          newTravelerInfoCtx.currentInfantId = updatedTraveler.id;
        }
      } else {
        // Add non-infants to selected list (multi-select for flights)
        newTravelerInfoCtx.selectedTravelerIds = [
          ...selectedTravelerIds,
          updatedTraveler.id,
        ];
      }

      const currentPassengerInformation = ctx[ParentState.passengerInformation];

      // Protect against funnels that don't have passengerInformation
      if (!currentPassengerInformation) {
        return {
          ...ctx,
          [ParentState.travelerInformation]: newTravelerInfoCtx,
        };
      }

      return {
        ...ctx,
        [ParentState.travelerInformation]: newTravelerInfoCtx,
        // Sync to passengerInformation for flight checkout compatibility
        [ParentState.passengerInformation]: {
          ...currentPassengerInformation,
          selectedPassengerIds: newTravelerInfoCtx.selectedTravelerIds,
          selectedLapInfantIds: newTravelerInfoCtx.selectedLapInfantIds,
          infantOnlyError: newTravelerInfoCtx.infantOnlyError,
          currentInfantId: newTravelerInfoCtx.currentInfantId,
        },
      };
    }
  ),
  onInfantModalContinue: assign(
    (ctx: PartialParentContext, { seatType }: OnInfantModalContinueEvent) => {
      const travelerInformationCtx = ctx[ParentState.travelerInformation];

      // Get current infant ID and existing arrays
      const currentInfantId = travelerInformationCtx.currentInfantId;
      let selectedLapInfantIds =
        travelerInformationCtx.selectedLapInfantIds || [];
      let selectedTravelerIds = travelerInformationCtx.selectedTravelerIds;

      if (seatType === "OwnSeat") {
        // Remove from lap infants and add to regular travelers
        selectedLapInfantIds = selectedLapInfantIds.filter(
          (id: string) => id !== currentInfantId
        );

        selectedTravelerIds = [
          ...selectedTravelerIds,
          currentInfantId as string,
        ];
      } else if (seatType === "OnLap") {
        // Add to lap infants
        selectedLapInfantIds = [
          ...selectedLapInfantIds,
          currentInfantId as string,
        ];
      } else if (seatType === "NotTraveling") {
        // Remove from all selected IDs (infant is not traveling)
        selectedLapInfantIds = selectedLapInfantIds.filter(
          (id: string) => id !== currentInfantId
        );

        selectedTravelerIds = selectedTravelerIds.filter(
          (id: string) => id !== currentInfantId
        );
      }

      // Update the context
      const updatedContext = {
        ...travelerInformationCtx,
        selectedTravelerIds,
        selectedLapInfantIds,
      };

      const currentPassengerInformation = ctx[ParentState.passengerInformation];

      return {
        ...ctx,
        [ParentState.travelerInformation]: updatedContext,
        [ParentState.passengerInformation]: {
          ...currentPassengerInformation,
          selectedPassengerIds: updatedContext.selectedTravelerIds,
          selectedLapInfantIds: updatedContext.selectedLapInfantIds,
        },
      };
    }
  ),
};
